import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Container } from "../ui/Container";
import { cn } from "../../utils/cn";
import { PARTNER_REGISTER_URL } from "../../utils/appLinks";

export interface HeaderProps {
  className?: string;
  variant?: "transparent" | "solid";
}

const Header: React.FC<HeaderProps> = ({ className, variant }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  const isInnerPage = location.pathname !== "/";
  const isSolid = variant === "solid" || isInnerPage || isScrolled;

  // Handle scroll effect for sticky header and active section
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      const sections = ["deals", "how-it-works", "faq"];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isMenuOpen && !target.closest("[data-mobile-menu]")) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    if (href.startsWith("#")) {
      e.preventDefault();
      const targetId = href.replace("#", "");
      const targetElement = document.getElementById(targetId);

      if (targetElement) {
        const headerOffset = 80;
        const elementPosition = targetElement.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });

        setIsMenuOpen(false);
      }
    }
  };

  const navigationItems: Array<{
    label: string;
    href: string;
    isRoute: boolean;
    section?: string;
  }> = [
    { label: "Home", href: "/", isRoute: true },
    { label: "All Shops", href: "/shops", isRoute: true },
    { label: "Deals near you", href: "/deals", isRoute: true },
    { label: "FAQ", href: "/#faq", section: "faq", isRoute: false },
  ];

  const isItemActive = (href: string) => {
    if (href === "/") {
      return location.pathname === "/";
    }
    if (href === "/shops") {
      return (
        location.pathname === "/shops" ||
        location.pathname === "/all-shops" ||
        location.pathname.startsWith("/shops/")
      );
    }
    if (href === "/deals") {
      return (
        location.pathname === "/deals" ||
        location.pathname === "/deals-near-you" ||
        location.pathname.startsWith("/deals/")
      );
    }
    return location.pathname === href;
  };

  // Scroll to the App Store / Play Store banner and briefly pulse its badges
  const handleGetAppClick = () => {
    setIsMenuOpen(false);
    const badges = document.getElementById("download-badges");
    if (!badges) {
      // Pages without the banner: go to the homepage section instead
      navigate("/#download");
      return;
    }
    // window.scrollTo (not scrollIntoView) so only the page scrolls, centring the badges
    const rect = badges.getBoundingClientRect();
    window.scrollTo({
      top: Math.max(0, window.scrollY + rect.top - (window.innerHeight - rect.height) / 2),
      behavior: "smooth",
    });
    badges.classList.remove("badge-spotlight");
    window.setTimeout(() => {
      badges.classList.add("badge-spotlight");
      window.setTimeout(() => badges.classList.remove("badge-spotlight"), 3500);
    }, 600);
  };

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-[padding] duration-500 ease-out",
        isScrolled ? "pt-2.5 sm:pt-3 px-2.5 sm:px-4" : "pt-0 px-0",
        className
      )}
    >
      {/* Glass bar: full width at the top, floating rounded pill once scrolled */}
      <div
        className={cn(
          "mx-auto backdrop-blur-xl backdrop-saturate-150 border transition-all duration-500 ease-out",
          isScrolled
            ? "max-w-6xl rounded-full bg-[#1A0F33]/80 border-white/15 py-1 shadow-[0_18px_40px_-14px_rgba(10,0,30,0.7),inset_0_1px_0_rgba(255,255,255,0.12)]"
            : isSolid
            ? "max-w-full rounded-none bg-[#130B24]/85 border-transparent border-b-white/10 py-3"
            : "max-w-full rounded-none bg-white/[0.04] border-transparent border-b-white/10 py-3 sm:py-4"
        )}
      >
      <Container size="xl" padding="md" className={cn(isScrolled && "sm:px-5 lg:px-6")}>
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <div className="flex items-center">
            <Link
              to="/"
              onClick={() => {
                if (location.pathname === "/") {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
              className="flex items-center space-x-2.5 group transition-transform hover:scale-[1.02]"
            >
              {/* Setgo Brand Logo */}
              <img
                src="/Logo1.png"
                alt="SetGo Logo"
                className="h-5 sm:h-6 md:h-7 w-auto object-contain drop-shadow-sm group-hover:scale-105 transition-transform"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-7">
            {navigationItems.map((item) => {
              const active = isItemActive(item.href);

              return item.isRoute ? (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => {
                    if (location.pathname === item.href) {
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }
                  }}
                  className={cn(
                    "text-sm font-medium transition-all relative py-1",
                    active
                      ? "text-white font-semibold after:content-[''] after:absolute after:-bottom-1 after:left-0 after:right-0 after:h-0.5 after:bg-white after:rounded-full"
                      : "text-purple-200/80 hover:text-white"
                  )}
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className={cn(
                    "text-sm font-medium transition-colors",
                    activeSection === "faq"
                      ? "text-[#FFC727] font-semibold"
                      : "text-purple-200/80 hover:text-white"
                  )}
                >
                  {item.label}
                </a>
              );
            })}
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-3.5">
            <a
              href={PARTNER_REGISTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="glass-dark inline-flex items-center justify-center px-5 py-2 rounded-full text-xs sm:text-sm font-medium text-white hover:bg-white/15 transition-all duration-200 hover:border-white/30"
            >
              List your business
            </a>
            <button
              onClick={handleGetAppClick}
              className="sheen inline-flex items-center justify-center px-5 py-2 rounded-full text-xs sm:text-sm font-bold text-[#1E1B4B] bg-gradient-to-b from-[#FFD24D] to-[#F5B015] shadow-[0_8px_20px_-6px_rgba(251,191,36,0.55),inset_0_1px_0_rgba(255,255,255,0.5)] hover:shadow-[0_10px_26px_-6px_rgba(251,191,36,0.7),inset_0_1px_0_rgba(255,255,255,0.5)] transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]"
            >
              Get the App
            </button>
          </div>

          {/* Mobile / Tablet Actions (Below lg) */}
          <div className="flex lg:hidden items-center space-x-2.5">
            <button
              onClick={handleGetAppClick}
              className="inline-flex items-center justify-center px-3.5 py-1.5 rounded-full text-xs font-bold text-[#1E1B4B] bg-[#FBBF24] hover:bg-[#F59E0B] shadow-sm transition-all active:scale-95"
            >
              Get App
            </button>
            <button
              onClick={toggleMenu}
              className="p-2 rounded-xl text-purple-100 hover:text-white hover:bg-white/10 transition-colors"
              data-mobile-menu
              aria-label="Toggle menu"
            >
              <svg
                className={cn(
                  "w-6 h-6 transition-transform",
                  isMenuOpen && "rotate-90"
                )}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

      </Container>
      </div>

      {/* Mobile Navigation Menu (outside the pill so it isn't clipped by its rounding) */}
      <Container size="xl" padding={isScrolled ? "none" : "md"} className={cn(isScrolled && "max-w-6xl")}>
        <div
          className={cn(
            "lg:hidden transition-all duration-300 ease-in-out overflow-hidden",
            isMenuOpen ? "max-h-96 opacity-100 mt-2" : "max-h-0 opacity-0"
          )}
          data-mobile-menu
        >
          <nav className="py-4 px-3 space-y-2 rounded-2xl bg-[#2A164E]/80 border border-white/15 backdrop-blur-2xl backdrop-saturate-150 shadow-[0_24px_48px_-16px_rgba(8,0,24,0.7),inset_0_1px_0_rgba(255,255,255,0.12)]">
            {navigationItems.map((item) => {
              const active = isItemActive(item.href);
              return item.isRoute ? (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => {
                    setIsMenuOpen(false);
                    if (location.pathname === item.href) {
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }
                  }}
                  className={cn(
                    "block px-4 py-2.5 rounded-xl font-medium text-sm transition-all",
                    active
                      ? "text-[#FFC727] bg-white/10 font-semibold"
                      : "text-purple-100 hover:text-white hover:bg-white/10"
                  )}
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className={cn(
                    "block px-4 py-2.5 rounded-xl font-medium text-sm transition-all",
                    activeSection === item.section
                      ? "text-[#FFC727] bg-white/10 font-semibold"
                      : "text-purple-100 hover:text-white hover:bg-white/10"
                  )}
                >
                  {item.label}
                </a>
              );
            })}
            <div className="pt-3 space-y-2 border-t border-purple-500/20">
              <a
                href={PARTNER_REGISTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center px-4 py-2.5 rounded-full text-sm font-medium text-white bg-white/10 hover:bg-white/20 border border-white/20 transition-all"
              >
                List your business
              </a>
              <button
                onClick={handleGetAppClick}
                className="w-full inline-flex items-center justify-center px-4 py-2.5 rounded-full text-sm font-bold text-[#1E1B4B] bg-[#FBBF24] hover:bg-[#F59E0B] shadow-md transition-all"
              >
                Get the App
              </button>
            </div>
          </nav>
        </div>
      </Container>
    </header>
  );
};

export { Header };
